/**
 * Contenedor principal del listado de reubicaciones (consulta/seguimiento).
 * Basado en el flujo de OrderPreparation/List sin manifiesto ni búsqueda por texto.
 */
import { TotalResults } from '@shared/components/ui/TotalResults';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useCartsSimple } from '@modules/Carts/hooks';
import { useRelocationsMain } from '../../hooks/useRelocationsMain';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { CardList, RelocationsAgGridTable, RelocationsBreadcrumb } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { RelocationsFiltersSection } from '../interaction';

export const Main = () => {
  const { t } = useTranslation('relocationsModule');
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const handleFilterChangeWrapper = useCallback((conditions: FilterConditionOutput[]) => {
    setFilterConditions(conditions);
  }, []);

  const {
    selectedColumns,
    relocations,
    isLoading,
    isFetching,
    error,
    total,
    relocationsViewProps,
    handleColumnsChange,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    hasActiveFilters,
  } = useRelocationsMain(filterConditions);

  const { carts } = useCartsSimple({ limit: 100 });

  const cartLabelsById = useMemo<Record<number, string>>(() => {
    return carts.reduce<Record<number, string>>((acc, cart) => {
      const cartWithName = cart as typeof cart & { name?: string };
      const name = typeof cartWithName.name === 'string' ? cartWithName.name.trim() : '';
      const code = typeof cart.code === 'string' ? cart.code.trim() : '';
      acc[cart.id] = name || code || String(cart.id);
      return acc;
    }, {});
  }, [carts]);

  const { columnDefs, tanStackColumns } = useAgGridColumns(selectedColumns, cartLabelsById);
  const selectorColumns = useMemo(() => tanStackColumns, [tanStackColumns]);

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const renderTable = () => {
    if (isMobile) {
      return <CardList {...relocationsViewProps} selectedColumns={selectedColumns} cartLabelsById={cartLabelsById} />;
    }
    return (
      <RelocationsAgGridTable
        {...relocationsViewProps}
        columnDefs={columnDefs}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    );
  };

  if (isInitialLoad) {
    return <LoadingState message={t('loading.message')} />;
  }

  if (error) {
    return <ErrorState error={error as Error} onRetry={refetch} />;
  }

  return (
    <>
      <RelocationsBreadcrumb />
      <div className="px-6 pt-4">
        {/* Cabecera: filtros alineados con GET /relocations y selector de columnas */}
        <div className="mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <RelocationsFiltersSection onFilterChange={handleFilterChangeWrapper} />
              <ColumnSelector
                columns={selectorColumns}
                selected={selectedColumns}
                onColumnsChange={handleColumnsChange}
                fixedColumns={['id']}
                storageKey="relocations-ag-grid-table"
              />
            </div>
            <TotalResults total={total} />
          </div>
        </div>

        <div
          className={`transition-opacity duration-300 mb-4 mt-4 ${
            isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'
          } ${!isLoading && relocations.length === 0 ? 'h-0 overflow-hidden' : 'h-auto'}`}
        >
          {renderTable()}
        </div>

        {!isLoading && relocations.length === 0 && (
          <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.HANDS} />
        )}
      </div>
    </>
  );
};
